import {CommentIcon} from '@primer/octicons-react'
import React, {memo} from 'react'
import {Box, TreeView} from '@primer/react'
import {PortalTooltip} from '@github-ui/portal-tooltip/portalled'
import type {DiffDelta, DirectoryNode, FileNode} from './diff-file-tree-helpers'
import {getFileTree} from './diff-file-tree-helpers'
import {FileStatusIcon} from './FileStatusIcon'
import {useFileTreeTooltip} from '@github-ui/use-file-tree-tooltip'

interface FileProps {
  file: FileNode<DiffDelta>
  depth: number
  onSelect?(file: DiffDelta): void
  isActive: boolean
}

const File = memo(function File({file, depth, isActive, onSelect}: FileProps) {
  const rowRef = React.useRef<HTMLElement>(null)
  const showTooltip = useFileTreeTooltip({focusRowRef: rowRef, mouseRowRef: rowRef})

  const unresolvedCommentCount = file.diff.unresolvedCommentCount
  return (
    <TreeView.Item
      defaultExpanded
      aria-level={depth}
      current={isActive}
      id={file.diff.path}
      onSelect={() => onSelect?.(file.diff)}
      ref={rowRef}
    >
      <TreeView.LeadingVisual>
        <FileStatusIcon status={file.diff.changeType} />
      </TreeView.LeadingVisual>
      {file.fileName}
      {showTooltip && (
        <PortalTooltip
          data-testid={`${file.fileName}-item-tooltip`}
          id={`${file.fileName}-item-tooltip`}
          contentRef={rowRef}
          aria-label={file.fileName}
          open={true}
          direction="ne"
        />
      )}
      {/* Don't show the unresolved comment count if it's 0 */}
      {!!unresolvedCommentCount && (
        <TreeView.TrailingVisual>
          <Box sx={{alignItems: 'center', display: 'flex', flexDirection: 'row'}}>
            <CommentIcon />
            <Box sx={{ml: 1, fontWeight: 600, color: 'fg.default', fontSize: 0}}>
              {unresolvedCommentCount < 10 ? unresolvedCommentCount : '9+'}
            </Box>
          </Box>
        </TreeView.TrailingVisual>
      )}
    </TreeView.Item>
  )
})

interface DirectoryProps extends Pick<FileProps, 'onSelect'> {
  directory: DirectoryNode<DiffDelta>
  depth?: number
  leadingPath?: string
  activeFilePath?: string
}

function Directory({
  directory,
  depth = 0,
  leadingPath = '',
  activeFilePath,
  ...fileProps
}: DirectoryProps): JSX.Element {
  const pathPrefix = leadingPath ? `${leadingPath}/` : ''
  const rowRef = React.useRef<HTMLElement | null>(null)
  // Using listItemRef to fix a bug where two tooltips will stay rendered at the same time
  const listItemRef = React.useRef<HTMLElement>(null)
  const showTooltip = useFileTreeTooltip({focusRowRef: listItemRef, mouseRowRef: rowRef})

  // collapse this directory if it has no files and only one child directory
  if (!directory.files.length && directory.directories.length === 1) {
    return (
      <>
        {directory.directories.map(subDirectory => (
          <Directory
            key={subDirectory.path}
            activeFilePath={activeFilePath}
            /* Set depth to one for top-level directories that are collapsed so that tree styling is applied correctly */
            depth={depth === 0 ? 1 : depth}
            directory={subDirectory}
            leadingPath={`${pathPrefix}${directory.name}`}
            {...fileProps}
          />
        ))}
      </>
    )
  }

  function renderDirectoryContents() {
    return (
      <>
        {directory.files.map(file => {
          return (
            <File
              key={file.filePath}
              depth={depth + 1}
              file={file}
              isActive={activeFilePath === file.filePath}
              {...fileProps}
            />
          )
        })}
        {directory.directories.map(dir => {
          return (
            <Directory
              key={dir.path}
              activeFilePath={activeFilePath}
              depth={depth + 1}
              directory={dir}
              {...fileProps}
            />
          )
        })}
      </>
    )
  }

  if (depth === 0) {
    return renderDirectoryContents()
  }

  return (
    <TreeView.Item ref={listItemRef} key={directory.path} defaultExpanded id={directory.path}>
      <TreeView.LeadingVisual>
        <TreeView.DirectoryIcon />
      </TreeView.LeadingVisual>
      <span ref={rowRef}>{`${pathPrefix}${directory.name}`}</span>
      {showTooltip && (
        <PortalTooltip
          data-testid={`${directory.name}-directory-item-tooltip`}
          id={`${directory.name}-directory-item-tooltip`}
          contentRef={listItemRef}
          aria-label={`${pathPrefix}${directory.name}`}
          open={true}
          direction="ne"
        />
      )}
      <TreeView.SubTree>{renderDirectoryContents()}</TreeView.SubTree>
    </TreeView.Item>
  )
}

export interface DiffFileTreeProps extends Pick<FileProps, 'onSelect'> {
  activeFilePath?: string
  diffs: Readonly<Array<Readonly<DiffDelta>>>
}

export const DiffFileTree = memo(function FileTree({activeFilePath, diffs, ...fileProps}: DiffFileTreeProps) {
  const fileTree = getFileTree<DiffDelta>(diffs)
  return (
    <TreeView aria-label="File Tree">
      <Directory activeFilePath={activeFilePath} directory={fileTree} {...fileProps} />
    </TreeView>
  )
})

try{ File.displayName ||= 'File' } catch {}
try{ Directory.displayName ||= 'Directory' } catch {}
try{ DiffFileTree.displayName ||= 'DiffFileTree' } catch {}